import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Bell, ShieldCheck } from "lucide-react";
import { api } from "../api";
import { Panel, PageHead, Loading, LoadError, Badge, fmtDateTime, fmtDate } from "../ui";

const fieldCls =
    "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors";

const ConfirmDialog = ({ open, title, body, confirmLabel, onConfirm, onClose, testid, busy }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/60" />
            <div className="relative w-full max-w-md rounded-2xl border border-[#1D322E] bg-[#12201D] p-6 shadow-2xl" data-testid={testid}>
                <h3 className="text-lg font-semibold text-[#F7F5EF]">{title}</h3>
                <p className="mt-2 text-sm text-[#A3B8B0]">{body}</p>
                <div className="mt-6 flex gap-3">
                    <button type="button" onClick={onClose} data-testid={`${testid}-cancel`} className="flex-1 rounded-lg border border-[#1D322E] px-4 py-2.5 text-sm font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] transition-colors">
                        Cancel
                    </button>
                    <button type="button" onClick={onConfirm} disabled={busy} data-testid={`${testid}-confirm`} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-red-400 disabled:opacity-60">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const UsersRoles = ({ users, reload, notify }) => {
    const [busyId, setBusyId] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "COORDINATOR" });
    const [creating, setCreating] = useState(false);
    const [formError, setFormError] = useState("");
    const [created, setCreated] = useState(null);
    const [confirm, setConfirm] = useState(null); // {user, field, value, title, body}

    const applyChange = async () => {
        const { user: u, payload } = confirm;
        setBusyId(u.id);
        try {
            await api.patch(`/admin/coordinators/${u.id}`, payload);
            setConfirm(null);
            await reload();
            notify("User updated.");
        } catch (err) {
            const detail = err.response?.data?.detail;
            notify(typeof detail === "string" ? detail : "Could not update this user.", true);
            setConfirm(null);
        } finally {
            setBusyId("");
        }
    };

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        setFormError("");
        setCreated(null);
        try {
            const { data } = await api.post("/admin/coordinators", form);
            setCreated(data);
            setForm({ name: "", email: "", password: "", role: "COORDINATOR" });
            await reload();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setFormError(typeof detail === "string" ? detail : "Could not create the account.");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-6">
            <Panel className="lg:col-span-2 overflow-hidden" data-testid="users-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                {["Name", "Email", "Role", "Assigned cases", "Status", "Joined", "Actions"].map((h) => (
                                    <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                                    <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium whitespace-nowrap">{u.name}</td>
                                    <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <select
                                            data-testid={`user-role-select-${u.email}`}
                                            value={u.role}
                                            onChange={(e) => setConfirm({
                                                user: u,
                                                payload: { role: e.target.value },
                                                title: "Change role",
                                                body: `Change ${u.name}'s role from ${u.role} to ${e.target.value}? ${e.target.value === "ADMIN" ? "Admins have full access to all data." : "Coordinators see only their assigned cases."}`,
                                                confirmLabel: "Change role",
                                            })}
                                            disabled={busyId === u.id}
                                            className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-2.5 py-1.5 text-xs text-[#F7F5EF] outline-none focus:border-[#10B981] transition-colors"
                                        >
                                            <option value="ADMIN">ADMIN</option>
                                            <option value="COORDINATOR">COORDINATOR</option>
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-emerald-300 font-semibold">{u.assigned_cases}</td>
                                    <td className="px-4 py-3">
                                        {u.active ? (
                                            <Badge className="border-emerald-800/70 bg-emerald-950/60 text-emerald-300">Active</Badge>
                                        ) : (
                                            <Badge className="border-zinc-700/80 bg-zinc-800/80 text-zinc-400">Deactivated</Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            data-testid={`user-active-toggle-${u.email}`}
                                            onClick={() => setConfirm({
                                                user: u,
                                                payload: { active: !u.active },
                                                title: u.active ? "Deactivate account" : "Activate account",
                                                body: u.active
                                                    ? `${u.name} will no longer be able to sign in. Their assigned cases are kept.`
                                                    : `${u.name} will be able to sign in again.`,
                                                confirmLabel: u.active ? "Deactivate" : "Activate",
                                            })}
                                            disabled={busyId === u.id}
                                            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                                u.active
                                                    ? "border-red-900/60 text-red-300 hover:bg-red-950/40"
                                                    : "border-emerald-800/70 text-emerald-300 hover:bg-emerald-950/40"
                                            }`}
                                        >
                                            {busyId === u.id ? "…" : u.active ? "Deactivate" : "Activate"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <Panel className="p-5 h-fit" data-testid="user-create-form">
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                    <Plus className="w-4 h-4" /> Add user
                </h2>
                <form onSubmit={create} className="mt-4 space-y-4">
                    <input data-testid="user-create-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={fieldCls} />
                    <input data-testid="user-create-email" type="email" required placeholder="name@healease.health" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={fieldCls} />
                    <input data-testid="user-create-password" type="text" required minLength={8} placeholder="Temporary password (min 8 chars)" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={fieldCls} />
                    <select data-testid="user-create-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={fieldCls}>
                        <option value="COORDINATOR">Coordinator</option>
                        <option value="ADMIN">Admin</option>
                    </select>
                    {formError && (
                        <p role="alert" data-testid="user-create-error" className="rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                            {formError}
                        </p>
                    )}
                    {created && (
                        <p data-testid="user-create-success" className="rounded-lg border border-emerald-900/60 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-300">
                            {created.name} can now sign in with {created.email}. Share the temporary password securely — it is never shown again.
                        </p>
                    )}
                    <button type="submit" data-testid="user-create-submit" disabled={creating} className="w-full rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed">
                        {creating ? "Creating…" : "Create account"}
                    </button>
                </form>
            </Panel>

            <ConfirmDialog
                open={!!confirm}
                title={confirm?.title}
                body={confirm?.body}
                confirmLabel={confirm?.confirmLabel}
                onConfirm={applyChange}
                onClose={() => setConfirm(null)}
                testid="confirm-user-change"
                busy={!!confirm && busyId === confirm.user.id}
            />
        </div>
    );
};

const Notifications = ({ notify }) => {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings/notifications")
            .then((res) => setSettings(res.data))
            .catch(() => notify("Could not load notification settings.", true));
    }, []);

    const save = async (next) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/settings/notifications", next);
            setSettings(data);
            notify("Notification settings saved.");
        } catch (err) {
            notify("Could not save notification settings.", true);
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return <Loading />;
    const options = [
        ["new_case", "New case notification", "Alert the team when a new enquiry arrives."],
        ["case_assignment", "Case assignment notification", "Notify a coordinator when a case is assigned to them."],
        ["status_change", "Status change notification", "Notify the assigned coordinator when a case status changes."],
    ];

    return (
        <div className="max-w-2xl space-y-4" data-testid="notifications-section">
            <p className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3 text-xs leading-relaxed text-[#64716D]" data-testid="notifications-placeholder-note">
                Configuration placeholder — notifications are stored now and email/WhatsApp delivery will be connected in a later phase. Nothing is sent automatically today.
            </p>
            {options.map(([key, label, hint]) => (
                <label key={key} className="flex items-start justify-between gap-4 rounded-xl border border-[#1D322E] bg-[#0E1A17] px-4 py-3.5">
                    <span>
                        <span className="block text-sm font-semibold text-[#F7F5EF]" data-testid={`notification-label-${key}`}>{label}</span>
                        <span className="mt-0.5 block text-xs text-[#64716D]">{hint}</span>
                    </span>
                    <input
                        type="checkbox"
                        data-testid={`notification-toggle-${key}`}
                        checked={!!settings[key]}
                        onChange={(e) => save({ [key]: e.target.checked })}
                        disabled={saving}
                        className="mt-0.5 w-4 h-4 cursor-pointer"
                    />
                </label>
            ))}
        </div>
    );
};

const DemoData = ({ notify }) => {
    const [demoCount, setDemoCount] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Count demo cases (demo-tagged entries in the cases list).
        api.get("/admin/cases?limit=100")
            .then((res) => setDemoCount(res.data.items.filter((c) => c.demo).length))
            .catch(() => setDemoCount(0));
    }, []);

    const cleanup = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/cleanup-demo");
            setConfirmOpen(false);
            setDemoCount(0);
            notify(`Removed ${data.removed_cases} demo case(s) and ${data.removed_patients} demo patient(s). Real records were not touched.`);
        } catch (err) {
            notify("Could not clean up demo data.", true);
            setConfirmOpen(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-2xl space-y-4" data-testid="demo-data-section">
            <Panel className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-[#F7F5EF]">
                            <Trash2 className="w-4 h-4 text-amber-400" /> Demo data
                        </h3>
                        <p className="mt-1.5 text-xs leading-relaxed text-[#64716D] max-w-md">
                            {demoCount === null
                                ? "Counting demo records…"
                                : demoCount === 0
                                ? "No demo records in the system."
                                : `${demoCount} demo case${demoCount === 1 ? "" : "s"} (tagged DEMO) are currently in the system. Demo patients use @demo.healease.health emails and are never mixed with real records.`}
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="demo-cleanup-button"
                        disabled={busy || !demoCount}
                        onClick={() => setConfirmOpen(true)}
                        className="rounded-lg border border-red-900/60 px-4 py-2.5 text-xs font-bold text-red-300 transition-colors hover:bg-red-950/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Remove demo data
                    </button>
                </div>
            </Panel>
            <ConfirmDialog
                open={confirmOpen}
                title="Remove demo data"
                body="This permanently removes only demo-tagged cases, their notes, activities, referrals, communications and demo patients. Real enquiries and cases are never touched."
                confirmLabel="Remove demo data"
                onConfirm={cleanup}
                onClose={() => setConfirmOpen(false)}
                testid="confirm-demo-cleanup"
                busy={busy}
            />
        </div>
    );
};

const AuditLog = ({ audit }) => (
    <Panel className="overflow-hidden" data-testid="audit-table">
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                        {["When", "User", "Action", "Entity", "Reference", "Details"].map((h) => (
                            <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D]">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {audit.map((a, i) => (
                        <tr key={a.id || i} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                            <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                            <td className="px-4 py-3 text-sm text-[#F7F5EF] whitespace-nowrap">{a.user_name}</td>
                            <td className="px-4 py-3 text-sm text-[#A3B8B0]">{a.action?.replaceAll("_", " ")}</td>
                            <td className="px-4 py-3 text-sm text-[#A3B8B0]">{a.entity}</td>
                            <td className="px-4 py-3 text-xs font-mono text-emerald-300">{a.entity_id || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#64716D]">
                                {a.details && Object.keys(a.details).length
                                    ? Object.entries(a.details)
                                          .filter(([, v]) => v !== null && v !== undefined && v !== "")
                                          .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                                          .join(" · ")
                                          .slice(0, 140)
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </Panel>
);

export const AdminSettings = () => {
    const [tab, setTab] = useState("users");
    const [users, setUsers] = useState(null);
    const [audit, setAudit] = useState(null);
    const [error, setError] = useState("");
    const [toast, setToast] = useState(null); // {msg, isError}

    const notify = (msg, isError = false) => {
        setToast({ msg: msg, isError });
        window.clearTimeout(window.__settingsToastTimer);
        window.__settingsToastTimer = window.setTimeout(() => setToast(null), 5000);
    };

    const load = () => {
        return Promise.all([
            api.get("/admin/coordinators").then((r) => setUsers(r.data.items)),
            api.get("/admin/audit?limit=150").then((r) => setAudit(r.data.items)),
        ]).catch(() => setError("Could not load settings. Please refresh."));
    };

    useEffect(() => {
        load();
    }, []);

    if (error) return <LoadError message={error} />;
    if (!users || !audit) return <Loading />;

    const tabs = [
        ["users", "Users & Roles", "settings-tab-users"],
        ["notifications", "Notifications", "settings-tab-notifications"],
        ["demo", "Demo data", "settings-tab-demo"],
        ["audit", "Audit Log", "settings-tab-audit"],
    ];

    return (
        <div data-testid="admin-settings-page">
            <PageHead title="System Settings" subtitle="Users & roles, notification preferences, demo data and the audit trail." />

            <div className="flex flex-wrap gap-2 border-b border-[#1D322E] pb-px" data-testid="settings-tabs">
                {tabs.map(([key, label, testid]) => (
                    <button
                        key={key}
                        type="button"
                        data-testid={testid}
                        onClick={() => {
                            setTab(key);
                            setToast(null);
                        }}
                        className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                            tab === key ? "border-emerald-400 text-[#F7F5EF]" : "border-transparent text-[#A3B8B0] hover:text-[#F7F5EF]"
                        }`}
                    >
                        {key === "notifications" && <Bell className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />}
                        {key === "users" && <ShieldCheck className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />}
                        {label}
                    </button>
                ))}
            </div>

            {toast && (
                <p
                    role="status"
                    data-testid={toast.isError ? "settings-toast-error" : "settings-toast"}
                    className={`mt-4 rounded-lg px-4 py-3 text-sm ${
                        toast.isError
                            ? "border border-red-900/60 bg-red-950/40 text-red-300"
                            : "border border-emerald-900/60 bg-emerald-950/40 text-emerald-300"
                    }`}
                >
                    {toast.msg}
                </p>
            )}

            <div className="mt-6">
                {tab === "users" && <UsersRoles users={users} reload={load} notify={notify} />}
                {tab === "notifications" && <Notifications notify={notify} />}
                {tab === "demo" && <DemoData notify={notify} />}
                {tab === "audit" && <AuditLog audit={audit} />}
            </div>
        </div>
    );
};
